import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ShieldAlert, Plus, RefreshCw, Loader2, Play, Activity, AlertOctagon,
  Radar, ChevronRight, Sparkles, Boxes,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  SEV_STYLE, STATUS_STYLE, STATUS_LABEL, PURPOSE_LABEL, HEALTH_STYLE,
  timeAgo, sid,
} from '@/lib/sentinelConstants';
import ProcessHelp from '@/components/ProcessHelp';

function SummaryCard({ label, value, tone = 'default', testid, icon: Icon }) {
  const toneCls = tone === 'bad' ? 'text-rose-600 dark:text-rose-300'
    : tone === 'warn' ? 'text-amber-600 dark:text-amber-300'
    : tone === 'good' ? 'text-emerald-600 dark:text-emerald-300' : 'text-foreground';
  return (
    <div data-testid={testid} className="rounded-xl border border-border/60 bg-card/60 p-4 flex flex-col gap-1">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
        {Icon ? <Icon className="w-3.5 h-3.5" /> : null}{label}
      </div>
      <div className={`text-2xl font-semibold ${toneCls}`}>{value}</div>
    </div>
  );
}

export default function SentinelsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sentinels, setSentinels] = useState([]);
  const [summary, setSummary] = useState(null);
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState('all');
  const [busyId, setBusyId] = useState('');
  const [seeding, setSeeding] = useState(false);
  const [packs, setPacks] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getSentinels(modelId === 'all' ? '' : modelId);
      setSentinels(res.sentinels || []);
      setSummary(res.summary || null);
    } catch (e) {
      toast.error(e.message || 'Failed to load Sentinels');
    } finally {
      setLoading(false);
    }
  }, [modelId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.getSemanticModels().then((m) => setModels(m || [])).catch(() => {});
    api.getSentinelPacks().then((r) => setPacks(r.packs || [])).catch(() => {});
  }, []);

  const evaluate = async (s) => {
    setBusyId(s.sentinel_id);
    try {
      const r = await api.evaluateSentinel(s.sentinel_id);
      if (r.fired && r.material) {
        toast.warning(`${s.name}: fired — ${r.run?.severity || 'incident'} raised`);
      } else if (r.fired) {
        toast.info(`${s.name}: detected a change, but below the materiality bar`);
      } else {
        toast.success(`${s.name}: all detection methods within limits`);
      }
      load();
    } catch (e) {
      toast.error(e.message || 'Evaluation failed');
    } finally {
      setBusyId('');
    }
  };

  const seedDemo = async () => {
    setSeeding(true);
    try {
      const r = await api.seedSentinelDemo();
      if (r.ok) { toast.success(`Seeded a demo Sentinel on ${r.metric}`); load(); }
      else toast.error(r.error || 'Could not seed demo');
    } catch (e) {
      toast.error(e.message || 'Could not seed demo');
    } finally {
      setSeeding(false);
    }
  };

  const sev = summary?.severity_mix || { critical: 0, high: 0, medium: 0, low: 0 };
  const activePacks = packs.filter((p) => (p.status || 'active') === 'active');

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="sentinels-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Radar className="w-6 h-6 text-primary" /> Sentinels
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Governed, versioned monitoring objects. Each detects a material condition on a certified
            metric — with full evidence and reproducibility — and raises a managed incident. All
            detection is deterministic and stays inside your data boundary.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ProcessHelp
            testId="sentinels-help"
            title="Sentinels"
            section="sentinels"
            intro="Governed, versioned monitors that watch a certified metric and raise a managed incident when a change is both statistically real and material — deterministic, inside your data boundary."
            steps={[
              'Click New Sentinel (or "New from pack") to build a monitor on a model and metric.',
              'Add one or more detection methods and set their thresholds.',
              'Set the materiality bar, persistence, cooldown, severity and recipients.',
              'Save as draft to keep tuning, or Save & activate to start monitoring.',
              'Use Evaluate to run one now; open a Sentinel to Replay history. Findings land in Incidents — nothing is auto-executed.',
            ]}
          />
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/incidents')} data-testid="sentinels-incidents-link">
            <AlertOctagon className="w-4 h-4 mr-1.5" /> Incidents
            {summary?.open_incidents ? <Badge className="ml-1.5 bg-rose-500/15 text-rose-600 border-rose-500/40">{summary.open_incidents}</Badge> : null}
          </Button>
          <Button variant="outline" size="sm" onClick={load} data-testid="sentinels-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
          {packs.length ? (
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/sentinel-packs')} data-testid="sentinels-manage-packs">
              <Boxes className="w-4 h-4 mr-1.5" /> Packs
            </Button>
          ) : null}
          {activePacks.length ? (
            <Select onValueChange={(v) => navigate(`/admin/sentinels/new?pack=${encodeURIComponent(v)}`)}>
              <SelectTrigger className="w-40 h-9" data-testid="sentinels-from-pack"><SelectValue placeholder="New from pack" /></SelectTrigger>
              <SelectContent>{activePacks.map((p) => <SelectItem key={p.pack_id} value={p.pack_id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          ) : null}
          <Button size="sm" onClick={() => navigate('/admin/sentinels/new')} data-testid="sentinels-new-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Sentinel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard testid="sentinels-summary-active" label="Active" value={summary?.active ?? 0} icon={Activity} tone="good" />
        <SummaryCard testid="sentinels-summary-open" label="Open incidents" value={summary?.open_incidents ?? 0}
          tone={summary?.open_incidents ? 'bad' : 'default'} icon={AlertOctagon} />
        <div data-testid="sentinels-summary-severity" className="rounded-xl border border-border/60 bg-card/60 p-4 flex flex-col gap-1">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Open severity mix</div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {['critical', 'high', 'medium', 'low'].map((k) => (
              <Badge key={k} className={`${SEV_STYLE[k]} text-[11px]`}>{sev[k] || 0} {k}</Badge>
            ))}
          </div>
        </div>
        <div data-testid="sentinels-summary-health" className="rounded-xl border border-border/60 bg-card/60 p-4 flex flex-col gap-1">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Portfolio health</div>
          <Badge className={`${HEALTH_STYLE[summary?.health || 'healthy']} w-fit mt-1 text-sm capitalize`}>
            {summary?.health || 'healthy'}
          </Badge>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Select value={modelId} onValueChange={setModelId}>
          <SelectTrigger className="w-64" data-testid="sentinels-model-filter"><SelectValue placeholder="All models" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All models</SelectItem>
            {models.map((m) => <SelectItem key={m.model_id} value={m.model_id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" onClick={seedDemo} disabled={seeding} data-testid="sentinels-seed-demo"
          className="text-muted-foreground">
          {seeding ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
          Seed demo
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : sentinels.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border/60 py-16 text-center" data-testid="sentinels-empty">
          <ShieldAlert className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-sm text-muted-foreground mb-4">No Sentinels yet. Create one to start watching a certified metric.</p>
          <Button size="sm" onClick={() => navigate('/admin/sentinels/new')}><Plus className="w-4 h-4 mr-1.5" /> New Sentinel</Button>
        </div>
      ) : (
        <div className="rounded-xl border border-border/60 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr className="text-left">
                <th className="px-4 py-2.5 font-medium">Sentinel</th>
                <th className="px-4 py-2.5 font-medium">Purpose</th>
                <th className="px-4 py-2.5 font-medium">Metric</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium">Last run</th>
                <th className="px-4 py-2.5 font-medium">Open</th>
                <th className="px-4 py-2.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sentinels.map((s) => (
                <tr key={s.sentinel_id} className="border-t border-border/50 hover:bg-muted/20"
                  data-testid={`sentinel-row-${sid(s.name)}`}>
                  <td className="px-4 py-3">
                    <button className="font-medium hover:text-primary text-left flex items-center gap-1"
                      onClick={() => navigate(`/admin/sentinels/${s.sentinel_id}`)}
                      data-testid={`sentinel-open-${sid(s.name)}`}>
                      {s.name} <ChevronRight className="w-3.5 h-3.5 opacity-50" />
                    </button>
                    <div className="text-xs text-muted-foreground">{s.model_name} · v{s.version}</div>
                  </td>
                  <td className="px-4 py-3"><Badge variant="outline">{PURPOSE_LABEL[s.purpose] || s.purpose}</Badge></td>
                  <td className="px-4 py-3">{s.metric_label || s.metric}</td>
                  <td className="px-4 py-3"><Badge className={STATUS_STYLE[s.status]}>{STATUS_LABEL[s.status] || s.status}</Badge></td>
                  <td className="px-4 py-3 text-xs">
                    <div className="text-muted-foreground">{timeAgo(s.state?.last_evaluated_at)}</div>
                    {s.state?.last_severity ? <Badge className={`${SEV_STYLE[s.state.last_severity]} mt-1 text-[10px]`}>{s.state.last_severity}</Badge> : null}
                  </td>
                  <td className="px-4 py-3">
                    {s.open_incidents ? <Badge className="bg-rose-500/15 text-rose-600 border-rose-500/40">{s.open_incidents}</Badge> : <span className="text-muted-foreground">0</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" disabled={busyId === s.sentinel_id}
                      onClick={() => evaluate(s)} data-testid={`sentinel-evaluate-${sid(s.name)}`}>
                      {busyId === s.sentinel_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Play className="w-3.5 h-3.5 mr-1" /> Evaluate</>}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
